import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Alert, LinearProgress, Skeleton } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import { useTranslation } from "react-i18next";
import { API_BASE_URL } from "../../../config/apiConfig";
import TableSkeletonLoader from "../../../common/TableLoading";
import AppModal from "../../../components/AppModal";
import Pagination from "../../../components/Pagination";
import AppButton from "../../../shared/AppButton";
import AppTable from "../../../shared/AppTable";
import StatusOutcomePill from "../../../shared/StatusOutcomePill";

export const importIsActive = (status) => ["queued", "running", "stopping"].includes(status);
export const importCanStop = (status) => ["queued", "running"].includes(status);
export const importCanResume = (status) => ["failed", "paused"].includes(status);
export const importError = (error) => error?.response?.data?.error || error?.response?.data?.message || error?.message || "Request failed";
export const currentLocalDayRange = (now = new Date()) => {
  const from = new Date(now);
  from.setHours(0, 0, 0, 0);
  const to = new Date(from);
  to.setDate(to.getDate() + 1);
  return { createdFrom: from.toISOString(), createdTo: to.toISOString() };
};

const PROGRESS_SX = {
  height: 6,
  borderRadius: 999,
  backgroundColor: "#e2e8f0",
  "& .MuiLinearProgress-bar": { backgroundColor: "#0070C2", borderRadius: 999 },
};

const OUTLINED_BUTTON = "!border-gray-300 !text-gray-600";

const DETAIL_POLL_INTERVAL = 1 * 60 * 1000;

const FILE_INPUT =
  "block h-[28px] max-w-[360px] flex-1 cursor-pointer rounded-[4px] border border-gray-300 bg-gray-100 text-[12px] text-gray-700 file:mr-2 file:h-[28px] file:cursor-pointer file:rounded-[4px] file:border-0 file:bg-[#0070C2] file:px-3 file:text-[12px] file:font-semibold file:text-white hover:file:bg-[#005fa3] disabled:cursor-default disabled:opacity-60";

function options(signal) {
  const token = (localStorage.getItem("token") || "").replace(/"/g, "");
  return { signal, timeout: 30000, headers: { Authorization: `Bearer ${token}` } };
}

function SummaryCard({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
      <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-base font-semibold text-slate-800">{value ?? "—"}</p>
    </div>
  );
}

export default function PatientImportJobs({ open, onClose }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedId, setSelectedId] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [job, setJob] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(null);
  const [resuming, setResuming] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [historyError, setHistoryError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [resultsError, setResultsError] = useState("");
  const [results, setResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [errorsOnly, setErrorsOnly] = useState(true);
  const [cursors, setCursors] = useState([0]);
  const [nextCursor, setNextCursor] = useState(0);
  const [resultPages, setResultPages] = useState(1);
  const [resultHasMore, setResultHasMore] = useState(false);
  const [revision, setRevision] = useState(0);
  const [detailRevision, setDetailRevision] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const cursor = cursors[cursors.length - 1];
  const resultsPage = cursors.length;
  const jobStatus = job?.status;
  const busy = uploading || resuming || stopping || downloading;
  const refreshLabel = secondsLeft === null
    ? t("Refresh")
    : `${t("Refresh")} (${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")})`;

  useEffect(() => {
    if (!open || detailOpen) return;
    setJobsLoading(true);
    const controller = new AbortController();
    let timer;
    async function poll() {
      let keepPolling = true;
      try {
        const { createdFrom, createdTo } = currentLocalDayRange();
        const { data } = await axios.get(`${API_BASE_URL}/patient-imports?type=upload_patient&page=${page}&created_from=${encodeURIComponent(createdFrom)}&created_to=${encodeURIComponent(createdTo)}`, options(controller.signal));
        if (controller.signal.aborted) return;
        setJobs(data.data || []);
        setTotalPages(data.total_pages || 1);
        setHistoryError("");
        keepPolling = (data.data || []).some(item => importIsActive(item.status));
      } catch (e) {
        if (controller.signal.aborted) return;
        setHistoryError(importError(e));
      }
      if (controller.signal.aborted) return;
      setJobsLoading(false);
      if (keepPolling) timer = setTimeout(poll, 5000);
    }
    poll();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [open, detailOpen, page, revision]);

  useEffect(() => {
    if (!detailOpen || !selectedId) return;
    if (jobStatus && !importIsActive(jobStatus)) return;
    setDetailError("");
    const controller = new AbortController();
    async function load() {
      setDetailLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE_URL}/patient-imports/${encodeURIComponent(selectedId)}`, options(controller.signal));
        if (controller.signal.aborted) return;
        setJob(data);
        setDetailError("");
      } catch (e) {
        if (controller.signal.aborted) return;
        setDetailError(importError(e));
      } finally {
        if (!controller.signal.aborted) setDetailLoading(false);
      }
    }
    load();
    return () => controller.abort();
  }, [detailOpen, selectedId, detailRevision, jobStatus]);

  useEffect(() => {
    if (!detailOpen || !selectedId || !jobStatus) return;
    setResults([]);
    setResultsError("");
    const controller = new AbortController();
    async function load() {
      setResultsLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE_URL}/patient-imports/${encodeURIComponent(selectedId)}/results`, {
          ...options(controller.signal), params: { after_id: cursor, limit: 100, errors_only: errorsOnly },
        });
        if (controller.signal.aborted) return;
        setResults(data.data || []);
        setNextCursor(data.next_after_id || 0);
        setResultPages(data.total_pages || 1);
        setResultHasMore(Boolean(data.has_more));
        setResultsError("");
      } catch (e) {
        if (controller.signal.aborted) return;
        setResultsError(importError(e));
      } finally {
        if (!controller.signal.aborted) setResultsLoading(false);
      }
    }
    load();
    return () => controller.abort();
  }, [detailOpen, selectedId, jobStatus, cursor, errorsOnly, detailRevision]);

  // While the open job is still processing, count down to the next automatic refresh.
  useEffect(() => {
    if (!detailOpen || !importIsActive(jobStatus)) {
      setSecondsLeft(null);
      return;
    }
    const deadline = Date.now() + DETAIL_POLL_INTERVAL;
    setSecondsLeft(DETAIL_POLL_INTERVAL / 1000);
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0) {
        clearInterval(interval);
        setDetailRevision(value => value + 1);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [detailOpen, jobStatus, detailRevision]);

  function select(id) {
    setJob(null);
    setSelectedId(id);
    setCursors([0]);
    setNextCursor(0);
    setDetailOpen(true);
  }

  async function upload(event) {
    event.preventDefault();
    if (!file || !file.name.toLowerCase().endsWith(".xlsx")) {
      setError(t("Select an .xlsx file."));
      return;
    }
    if (file.size > 1024 * 1024 * 1024) {
      setError(t("The maximum request size is 1 GiB. Split large workbooks."));
      return;
    }
    setUploading(true);
    setUploadPercent(null);
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("type", "upload_patient");
      const { data } = await axios.post(`${API_BASE_URL}/uploadExcel`, body, {
        ...options(), timeout: 0,
        onUploadProgress: (event) => setUploadPercent(event.total ? Math.round(event.loaded * 100 / event.total) : null),
      });
      if (!data.job_id) throw new Error("The server did not return a job ID. Deploy the background import API first.");
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setPage(1);
      setRevision(value => value + 1);
      select(data.job_id);
    } catch (e) {
      setError(importError(e) + " " + t("If the connection was interrupted, check Import Jobs before uploading again to avoid duplicates."));
    } finally {
      setUploading(false);
      setUploadPercent(null);
    }
  }

  async function downloadIssues() {
    if (!job || Number(job.failed) < 1) return;
    setDownloading(true);
    setDetailError("");
    try {
      const response = await axios.get(`${API_BASE_URL}/patient-imports/${encodeURIComponent(job.job_id)}/issues.xlsx`, {
        ...options(), responseType: "blob", timeout: 0,
      });
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `patient-import-issues-${job.job_id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      setDetailError(importError(e));
    } finally {
      setDownloading(false);
    }
  }

  async function stop() {
    if (!job || !importCanStop(job.status)) return;
    setStopping(true);
    setDetailError("");
    try {
      const { data } = await axios.post(`${API_BASE_URL}/patient-imports/${encodeURIComponent(job.job_id)}/stop`, {}, options());
      setJob(current => current ? { ...current, ...data } : data);
      setRevision(value => value + 1);
      setDetailRevision(value => value + 1);
    } catch (e) {
      setDetailError(importError(e));
    } finally {
      setStopping(false);
    }
  }

  async function resume() {
    if (!job || !importCanResume(job.status)) return;
    setResuming(true);
    setDetailError("");
    try {
      const { data } = await axios.post(`${API_BASE_URL}/patient-imports/${encodeURIComponent(job.job_id)}/retry`, {}, options());
      setJob(current => current ? { ...current, status: "queued", ...data } : current);
      setRevision(value => value + 1);
      setDetailRevision(value => value + 1);
    } catch (e) {
      setDetailError(importError(e));
    } finally {
      setResuming(false);
    }
  }

  async function changeResultsPage(nextPage) {
    if (nextPage === resultsPage || nextPage < 1 || nextPage > resultPages) return;
    if (nextPage < resultsPage) {
      setCursors(value => value.slice(0, nextPage));
      return;
    }
    if (!resultHasMore) return;
    setResultsLoading(true);
    setResultsError("");
    const chain = [];
    let afterId = nextCursor;
    try {
      for (let step = resultsPage; step < nextPage; step++) {
        const { data } = await axios.get(`${API_BASE_URL}/patient-imports/${encodeURIComponent(selectedId)}/results`, {
          ...options(), params: { after_id: afterId, limit: 100, errors_only: errorsOnly },
        });
        chain.push(afterId);
        afterId = data.next_after_id || 0;
        if (!data.has_more) break;
      }
      setCursors(value => [...value, ...chain]);
    } catch (e) {
      setResultsError(importError(e));
    } finally {
      setResultsLoading(false);
    }
  }

  const closeDetails = () => {
    if (busy) return;
    setDetailOpen(false);
    setSelectedId("");
    setJob(null);
    setCursors([0]);
    setNextCursor(0);
  };

  const close = () => {
    if (busy) return;
    closeDetails();
    setPage(1);
    onClose();
  };

  const jobColumns = [
    { header: t("Submitted"), render: (row) => new Date(row.created_at).toLocaleString() },
    { header: t("Status"), render: (row) => <StatusOutcomePill value={row.status} /> },
    { header: t("Processed"), align: "center", render: (row) => Number(row.processed).toLocaleString() },
    { header: t("Succeeded"), align: "center", render: (row) => Number(row.succeeded).toLocaleString() },
    {
      header: t("Failed"),
      align: "center",
      render: (row) => (
        <span className={Number(row.failed) > 0 ? "font-semibold text-red-600" : undefined}>
          {Number(row.failed).toLocaleString()}
        </span>
      ),
    },
  ];

  const resultColumns = [
    { header: t("Document ID"), render: (row) => <span className="font-mono text-[11px]">{row.document_id}</span> },
    {
      header: t("Patient"),
      render: (row) => <span className="font-mono text-[11px]">{row.patient_name || "—"}</span>,
      cellSx: { whiteSpace: "normal", wordBreak: "break-all", maxWidth: "180px" },
    },
    {
      header: t("Result"),
      render: (row) => row.error
        ? <span className="font-medium text-red-600">{row.error}</span>
        : <span className="font-medium text-green-700">{t("Succeeded")}</span>,
      cellSx: { whiteSpace: "normal", minWidth: "240px" },
    },
  ];

  return (
    <>
      <AppModal
        open={open && !detailOpen}
        onClose={close}
        title={t("Patient Excel Import Jobs")}
        maxWidth="lg"
        contentSx={{ backgroundColor: "#f8fafc" }}
      >
        <form onSubmit={upload} className="rounded-lg border border-slate-200 bg-white p-3">
          <p className="text-[12px] text-slate-600">{t("Upload an .xlsx workbook. Processing continues in the background after the file is saved, even if you close this window.")}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef}
              aria-label={t("Excel workbook")}
              type="file"
              accept=".xlsx"
              disabled={uploading}
              onChange={event => setFile(event.target.files?.[0] || null)}
              className={FILE_INPUT}
            />
            <AppButton
              type="submit"
              label={t("Upload Excel")}
              startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
              isLoading={uploading}
              loadingText={t("Uploading…")}
              disabled={uploading || resuming || stopping || !file}
            />
          </div>
          {file && <p className="mt-1.5 break-all text-[11px] font-semibold text-slate-600">{file.name}</p>}
          {uploading && <div className="mt-2" role="status">
            <LinearProgress variant={uploadPercent === null ? "indeterminate" : "determinate"} value={uploadPercent || 0} sx={PROGRESS_SX} />
            <p className="mt-1 text-[11px] text-slate-500">{uploadPercent === 100 ? t("File transferred; waiting for the server to save and queue it…") : t("File upload") + (uploadPercent === null ? "" : ` ${uploadPercent}%`)}</p>
          </div>}
        </form>

        {error && <Alert severity="error" className="mt-3">{error}</Alert>}
        {historyError && <Alert severity="error" className="mt-3">{historyError}</Alert>}

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 mb-1">
          <h3 className="text-[13px] font-bold text-slate-800">{t("Today’s import jobs")}</h3>
          <AppButton
            label={t("Refresh")}
            variant="outlined"
            startIcon={<RefreshIcon sx={{ fontSize: 17 }} />}
            onClick={() => setRevision(value => value + 1)}
            isLoading={jobsLoading}
            loadingText={t("Refreshing…")}
            disabled={busy}
            className={OUTLINED_BUTTON}
          />
        </div>

        <AppTable
          columns={jobColumns}
          data={jobs}
          loading={jobsLoading && jobs.length === 0}
          LoaderComponent={<TableSkeletonLoader columnCount={jobColumns.length + 1} rowCount={5} />}
          onView={(row) => select(row.job_id)}
        />

        <div className="mt-2">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>

        <div className="mt-4 flex justify-end gap-2 border-t border-slate-200 pt-2">
          <AppButton label={t("Close")} variant="outlined" onClick={close} disabled={busy} className={OUTLINED_BUTTON} />
        </div>
      </AppModal>

      <AppModal
        open={detailOpen}
        onClose={closeDetails}
        title={t("Import Job Details")}
        maxWidth="lg"
        contentSx={{ backgroundColor: "#f8fafc" }}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] text-slate-500">{t("Job ID")}: <span className="font-mono break-all">{selectedId}</span></p>
          <AppButton
            label={refreshLabel}
            variant="outlined"
            startIcon={<RefreshIcon sx={{ fontSize: 17 }} />}
            onClick={() => setDetailRevision(value => value + 1)}
            isLoading={detailLoading}
            loadingText={t("Refreshing…")}
            disabled={busy || !importIsActive(jobStatus)}
            className={OUTLINED_BUTTON}
          />
        </div>

        {detailError && <Alert severity="error" className="mt-2">{detailError}</Alert>}
        {!job && !detailError && <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-4" role="status" aria-label={t("Loading progress…")}>
          {[0, 1, 2, 3].map(key => <Skeleton key={key} variant="rounded" height={62} />)}
        </div>}

        {job && <>
          <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-4" aria-live="polite">
            <SummaryCard label={t("Status")} value={<StatusOutcomePill value={job.status} />} />
            <SummaryCard label={t("Processed")} value={Number(job.processed).toLocaleString()} />
            <SummaryCard label={t("Succeeded")} value={Number(job.succeeded).toLocaleString()} />
            <SummaryCard label={t("Failed")} value={Number(job.failed).toLocaleString()} />
          </div>

          {importIsActive(job.status) && <LinearProgress className="mt-2" sx={PROGRESS_SX} />}
          {job.error && <Alert severity="error" className="mt-2">{job.error}</Alert>}

     

          {importCanStop(job.status) && <div className="mt-3">
            <AppButton
              label={t("Stop import")}
              bgColor="#dc2626"
              startIcon={<StopCircleOutlinedIcon sx={{ fontSize: 17 }} />}
              onClick={stop}
              isLoading={stopping}
              loadingText={t("Stopping…")}
              disabled={busy}
            />
            <p className="mt-1 text-[11px] text-slate-500">{t("The worker will stop after the current operation and preserve the last committed checkpoint. You can resume this job later.")}</p>
          </div>}

          {importCanResume(job.status) && <div className="mt-3">
            <AppButton
              label={t("Resume from checkpoint")}
              startIcon={<ReplayOutlinedIcon sx={{ fontSize: 17 }} />}
              onClick={resume}
              isLoading={resuming}
              loadingText={t("Resuming…")}
              disabled={busy}
            />
            <p className="mt-1 text-[11px] text-slate-500">{t("Fix the job-level issue first. Resume preserves previous results and continues after the last committed row. A row interrupted before its checkpoint may be repeated.")}</p>
          </div>}

          {job.status === "completed_with_errors" && <Alert severity="warning" className="mt-2">{t("Processing finished. Correct failed rows and upload only those rows as a new workbook. Resume does not retry individual row errors.")}</Alert>}
        </>}
<div className="flex !w-full justify-between gap-2">
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <label className="flex w-max cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={errorsOnly}
              onChange={event => { setErrorsOnly(event.target.checked); setCursors([0]); }}
              className="h-3.5 w-3.5 cursor-pointer rounded border-gray-300 accent-[#0070C2] focus:ring-0"
            />
            <span className="text-[12px] font-semibold text-gray-700">{t("Show errors only")}</span>
          </label>
        </div>
          {Number(job?.failed) > 0 && <div className="my-2">
            <AppButton
              label={t("Download issues Excel")}
              variant="outlined"
              startIcon={<DownloadIcon sx={{ fontSize: 17 }} />}
              onClick={downloadIssues}
              isLoading={downloading}
              loadingText={t("Downloading issues…")}
              disabled={busy}
              className={OUTLINED_BUTTON}
            />
          </div>}
</div>
        {resultsError && <Alert severity="error" className="mt-1">{resultsError}</Alert>}
        <AppTable
          columns={resultColumns}
          data={results}
          loading={resultsLoading && results.length === 0}
          LoaderComponent={<TableSkeletonLoader columnCount={resultColumns.length} rowCount={5} />}
          actions={false}
        />

        <div className="mt-2">
          <Pagination currentPage={resultsPage} totalPages={resultPages} onPageChange={changeResultsPage} />
        </div>

        <div className="mt-4 flex justify-end gap-2 border-t border-slate-200 pt-2">
          <AppButton label={t("Close")} variant="outlined" onClick={closeDetails} disabled={busy} className={OUTLINED_BUTTON} />
        </div>
      </AppModal>
    </>
  );
}
